import { handleApiResult } from '@store/error';
import { apiSlice, toQueryParams } from './apiSlice';

export type SeatUsageApp = 'supar' | 'composer';

/**
 * One application's use of a licence on one UTC day. Counts only: no device name, id or address.
 * A day with no row had no activity, and reads as zero.
 */
export interface SeatUsageDay {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  app: SeatUsageApp;
  /** Seats licensed that day, the highest value seen. */
  seatLimit: number;
  /** Most seats held at once, online, to the 15 minutes the server samples at. */
  peakSeatsInUse: number;
  /** Requests refused because every seat was taken. */
  refusedRequests: number;
  /** Offline time that started this day, added when the device reports it, possibly days later. */
  offlineSeconds: number;
  /** The part of it used while every seat was taken. */
  offlineBeyondLimitSeconds: number;
  /** Most devices working offline at once while every seat was taken. */
  peakBeyondLimit: number;
  /** Decided by the server: every seat held, somebody refused, or the limit exceeded. */
  atLimit: boolean;
  /** Decided by the server: more seats held than licensed, or offline time beyond the limit. */
  exceeded: boolean;
}

export interface SeatUsageSummary {
  /** The highest limit of any day in the range. */
  seatLimit: number;
  /**
   * The limit in force for the range's first days without a row: that of the last day before the
   * range with one, else that of the range's first row, carried back.
   */
  seatLimitBefore: number;
  peakSeatsInUse: number;
  /** The most devices at once on one day, as the server counts them. */
  peakUsage: number;
  daysAtLimit: number;
  daysExceeded: number;
  /** The most by which one day's peak usage went past its limit. */
  exceededBy: number;
  refusedRequests: number;
  offlineSeconds: number;
  offlineBeyondLimitSeconds: number;
}

export interface SeatUsageHistory {
  licenseId: string;
  from: string;
  to: string;
  /** Days with activity before the latest five years were left out; `from` is where it starts. */
  truncated: boolean;
  /** Days with activity, oldest first. */
  days: SeatUsageDay[];
  /** Per application that has ever been used on the licence. */
  summary: Partial<Record<SeatUsageApp, SeatUsageSummary>>;
}

export interface SeatUsageQuery {
  licenseId: string;
  from?: string;
  to?: string;
}

export const seatUsageApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSeatUsage: builder.query<SeatUsageHistory, SeatUsageQuery>({
      query: ({ licenseId, ...params }) => ({ url: `/v1/licenses/${licenseId}/seat-usage`, params: toQueryParams(params) }),
      onQueryStarted: handleApiResult(),
    }),
  }),
});

export const { useGetSeatUsageQuery } = seatUsageApiSlice;
